import { EMAIL_RE, PASSWORD_RE } from '../utils/valueFormats';
import React, { useEffect } from 'react';
import { redirect, Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useRef, useCallback } from 'react';
import {
    Container, Typography, FormControl, FormGroup, FormControlLabel, Tooltip, Table, TableBody, TableCell, Link,
    Checkbox, InputLabel, Select, MenuItem, RadioGroup, Radio, Button, Fade, TableContainer, TableHead, Snackbar,
    useMediaQuery, Grid, TextField, Autocomplete, Chip, CircularProgress, DialogContentText, TableRow,
    Backdrop, FormHelperText, Paper, Divider, List, ListItem, ListItemText, LinearProgress, Alert, InputAdornment,
    ListItemButton, ListItemIcon, Box, Dialog, DialogTitle, DialogContent, DialogActions, GlobalStyles
} from '@mui/material';
import fetchData from '../utils/fetchData';
import { getAuth, reload, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut } from "firebase/auth";
import TurnStile from '../components/TurnStile';
import { auth, db } from '../config/firebase';
import { collection, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import logo_no_tagline from "../assets/logo_no_tagline.jpg";
import getIdToken from '../components/getIdToken';
import addUserToBackendIfFirstTime from '../utils/addUserToBackendIfFirstTime';
import { useCredits } from '../contexts/CreditsContext';
import addUserToFirestoreIfNotExists from '../utils/addUserToFirestoreIfNotExists';
import completeShopifyConnect from '../utils/completeShopifyConnect';
import readConnectionId from '../utils/readShopifyConnectionId';
import SignInWithGoogle from '../components/SignInWithGoogle';
import {
    initializeRedirect,
    handlePostAuthRedirect
} from '../components/redirectHandler';
import { notifySeverity } from '../utils/snackbarSeverity';

const SignUp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [signUpError, setSignUpError] = useState({ email: '', password: '' });
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [widgetPassed, setWidgetPassed] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [token, setToken] = useState(null);
    const [tokenValue, setTokenValue] = useState(null);
    const [verificationPromptOpen, setVerificationPromptOpen] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const tokenRef = useRef(null);
    const turnstileRef = useRef(null);
    const humanCheckRef = useRef(null); // the Turnstile box, for SignInWithGoogle to scroll to

    // Stable callbacks for <TurnStile>. Its effect re-renders the widget whenever
    // these change, and inline arrows changed on every render of this page, so any
    // state update (a snackbar, a keystroke) removed and re-created the widget —
    // wiping a "Verify you are human" checkbox the user was asked to tick.
    const onTurnstileToken = useCallback((token) => { tokenRef.current = token; setTokenValue(token); setWidgetPassed(!!token); }, []);
    const onTurnstileError = useCallback(({ message }) => {
        tokenRef.current = null;
        setTokenValue(null);
        setWidgetPassed(false);
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    }, []);
    const [isSignIn, setIsSignIn] = useState(false);
    const [isEmailSignUp, setIsEmailSignUp] = useState(false);
    const { refreshCredits } = useCredits();
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Handle redirect parameter on component mount
    useEffect(() => {
        initializeRedirect(searchParams);
    }, [searchParams]);


    useEffect(() => {
        if (!verificationPromptOpen) return;

        let intervalId;

        const checkVerification = async () => {
            try {
                const auth = getAuth();
                const user = auth.currentUser;
                if (!user) {
                    setSnackbarMessage("No user detected.");
                    setSnackbarOpen(true);
                    clearInterval(intervalId);
                    return;
                }

                await reload(user);

                if (user.emailVerified) {
                    await signInWithEmailAndPassword(auth, email, password);

                    const stored = readConnectionId();
                    if (stored?.value) {
                        await completeShopifyConnect(stored?.value, user.uid);
                    }

                    clearInterval(intervalId);

                    await addUserToFirestoreIfNotExists(user.uid, user.email, user.providerData);
                    await addUserToBackendIfFirstTime(user.uid, user.email, refreshCredits);

                    // Use redirect handler for post-auth navigation
                    handlePostAuthRedirect(navigate, true);
                }
                // else: still not verified, keep polling
            } catch (error) {
                console.error("Error checking verification:", error);
                setSnackbarMessage("Error checking verification. Please login manually.");
                setSnackbarOpen(true);
                clearInterval(intervalId);
            }
        };

        intervalId = setInterval(checkVerification, 3000);
        return () => clearInterval(intervalId);

    }, [verificationPromptOpen]);

    const checkUserStatus = async (uid) => {
        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${uid}`;

            const response = await fetchData({
                url: apiUrl,
            });

            if (!response?.error) {
                // User found, check if isBlocked is true
                if (response.user.isBlocked) {
                    setSnackbarMessage(
                        "Your account has been temporarily blocked/deleted. Please contact admin at dev@webis.ca."
                    );
                    setSnackbarOpen(true);

                    setIsLoggedIn(false);
                    // Sign the user out
                    const auth = getAuth();
                    await signOut(auth);

                    return false; // Block login
                }
            } else {
                setSnackbarMessage(
                    "Your account cannot be found. Please contact admin at dev@webis.ca."
                );
                setSnackbarOpen(true);

                setIsLoggedIn(false);
            }

            return true; // Allow login if no issues
        } catch (error) {
            console.error("Error checking user status:", error);

            const msg = error.message || "";

            if (msg.includes("NetworkError") || msg.includes("ERR_CONNECTION_REFUSED")) {
                setSnackbarMessage(
                    "Failed to connect. You might be blocked by security verification."
                );
            } else {
                setSnackbarMessage(
                    "Failed checking user status. Please try again later."
                );
            }

            setSnackbarOpen(true);
            setIsLoggedIn(false);
            return false;
        }
    };

    const waitForToken = (timeoutMs = 3000, intervalMs = 500) => {
        return new Promise((resolve) => {
            const start = Date.now();

            const check = () => {
                if (tokenRef.current) {
                    resolve(tokenRef.current);
                } else if (Date.now() - start >= timeoutMs) {
                    resolve(null);
                } else {
                    setTimeout(check, intervalMs);
                }
            };

            check();
        })
    }

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const newTokenValue = await waitForToken(10000, 500);
            if (!cancelled) {
                setTokenValue(newTokenValue);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, []);

    const sendVerifyEmail = async (email) => {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/sendVerifyEmail`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: email }),
        });
    }


    useEffect(() => {
        const endTime = Date.now() + countdown * 1000;

        const timer = setInterval(() => {
            const remaining = Math.round((endTime - Date.now()) / 1000);
            if (remaining >= 0) {
                setCountdown(remaining);
            } else {
                clearInterval(timer);
                setCountdown(0);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [countdown]);

    const handleResend = async () => {
        const auth = getAuth();
        try {
            if (auth.currentUser) {
                await sendVerifyEmail(auth.currentUser.email);
                setVerificationPromptOpen(true);
                setSnackbarMessage("Email resent successfully! Please check your email.");
                setSnackbarOpen(true);
                setCountdown(60);
            } else {
                setSnackbarMessage("Failed fetching user. Please try again later.");
                setSnackbarOpen(true);
                console.error("Failed fetching user");
            }
        } catch (error) {
            console.error("Error resending verification email:", error);
            setSnackbarMessage("Failed to resend email. Please try again later.");
            setSnackbarOpen(true);
        }
    };

    const signUp = async (event) => {
        event.preventDefault();
        setIsSignIn(true);
        setIsEmailSignUp(true);

        const newErrors = { email: '', password: '' };
        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (!validatePassword(password)) {
            newErrors.password = 'Password must be at least 6 characters, include letters and numbers';
        }

        if (newErrors.email || newErrors.password) {
            setSignUpError(newErrors);
            setIsSignIn(false);
            setIsEmailSignUp(false);
            return;
        }

        if (tokenValue == null) {
            setSnackbarMessage("Waiting to verify you are human.");
            setSnackbarOpen(true);
            setIsSignIn(false);
            setIsEmailSignUp(false);
            return;
        }


        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/botVerification`;

        // Guarded like Home's: a dropped connection or a non-JSON reply (a WAF
        // page, a 5xx) threw out of this handler and left the spinner up for good.
        let resData = null;
        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ Token: tokenValue }),
            });
            resData = await response.json().catch(() => null);
        } catch (err) {
            console.error('botVerification failed:', err);
            setSnackbarMessage("Couldn't reach the server to verify you. Check your connection and try again.");
            setSnackbarOpen(true);
            setIsSignIn(false);
            setIsEmailSignUp(false);
            return;
        }

        if (!resData?.success) {
            setSnackbarMessage("Turnstile verification failed!");
            setSnackbarOpen(true);
            setIsSignIn(false);
            setIsEmailSignUp(false);
            return;
        }

        try {
            const auth = getAuth();
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // const actionCodeSettings = {
            //     url:
            //         process.env.NODE_ENV === "development"
            //             ? "http://localhost:3000/verification"
            //             : "https://app.xeoai.com/verification", // your production domain
            //     handleCodeInApp: true, // this tells Firebase to route to your app
            // };

            // await sendEmailVerification(user, actionCodeSettings);
            // Send email verification
            await sendVerifyEmail(user.email);
            setIsSignIn(false);
            setIsEmailSignUp(false);
            setVerificationPromptOpen(true);
            setCountdown(60);

            /*const apiUrl = `${process.env.REACT_APP_API_BASE_URL}signup`;

            await fetchData({
                url: apiUrl,
                method: 'POST',
                body: { firebaseUid: user.uid, email: email, password: password }
            });
            */
            //navigate('/dashboard/domains?refreshCredits=true');

        } catch (error) {
            turnstileRef.current?.resetToken();
            let message = "Failed to create new account, try again later.";

            if (error.code === "auth/email-already-in-use") {
                message = "This email is already registered. Please log in instead.";
            } else if (error.code === "auth/weak-password") {
                message = "Password is too weak. Please choose a stronger password.";
            } else if (error.code === "auth/invalid-email") {
                message = "Invalid email format.";
            }

            setSnackbarMessage(message);
            setSnackbarOpen(true);
            setIsSignIn(false);
            setIsEmailSignUp(false);
        }
    }




    /*
     * The app's rule, not a looser local one. This was `/^\S+@\S+\.\S+$/`, and
     * `\S` matches an @ — so "a@b@c.d" was accepted at sign-up and refused by
     * every screen afterwards.
     */
    const validateEmail = (email) => EMAIL_RE.test(String(email ?? '').trim());

    /*
     * Exactly what the helper text says: 6+ characters, a letter and a digit —
     * any characters otherwise (Firebase only needs 6). This allowed only
     * letters, digits and !@#$%^&*()_+.,?- so a password the phone's manager
     * generated here (autoComplete="new-password") with ~ = } : ; " ' > ] in it,
     * as Firefox's and 1Password's do, was refused for being too short or
     * lacking letters and numbers — neither of which was true.
     */
    const validatePassword = (password) => PASSWORD_RE.test(password);

    return (
        <Box sx={{
            // Fill the viewport exactly with position:fixed + inset:0 so no
            // scrollbar can ever appear. Content sits slightly above center.
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            // Scrolls INSIDE the frame. It was overflow:hidden, so whatever didn't
            // fit — a phone in landscape, or the form with the keyboard up — was
            // cut off with no way to reach it. Scrollbars are hidden app-wide, so
            // on a screen where it fits nothing changes.
            overflowX: 'hidden',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            backgroundColor: 'var(--surface)',
            backgroundImage:
                'radial-gradient(ellipse 55% 38% at 50% 60%, rgba(25,118,210,0.12), transparent 70%),' +
                'radial-gradient(circle at 100% 0%, rgba(25,118,210,0.06), transparent 35%),' +
                'radial-gradient(circle at 0% 100%, rgba(13,71,161,0.05), transparent 40%)',
            px: { xs: 2.5, sm: 3 },
            pt: { xs: '5vh', sm: '8vh' },
            pb: 2,
        }}>
            <Fade in timeout={500}>
            <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                <Box component="img" src={logo_no_tagline} alt="XEO"
                    sx={{
                        width: { xs: 110, sm: 130 },
                        height: 'auto',
                        mixBlendMode: 'multiply',
                        WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                        maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                    }} />

                {!verificationPromptOpen && (
                    <>
                        <Box
                            sx={{
                                width: '100%',
                                backgroundColor: 'var(--surface)',
                                px: { xs: 3, sm: 4.5 },
                                py: { xs: 3, sm: 3.5 },
                                borderRadius: 3,
                                border: '1px solid var(--rule)',
                                boxShadow: '0 20px 60px rgba(15,31,61,0.10), 0 4px 16px rgba(25,118,210,0.06)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.6rem' }, fontWeight: 800, color: 'var(--ink)', textAlign: 'center', mb: 0.75, letterSpacing: '-0.015em' }}>
                                Create your account
                            </Typography>
                            <Typography sx={{ fontSize: '0.88rem', color: 'var(--ink-3)', textAlign: 'center', mb: 3.5 }}>
                                Get started with XEO in seconds
                            </Typography>
                            <SignInWithGoogle
                                checkUserStatus={checkUserStatus}
                                setSnackbarMessage={setSnackbarMessage}
                                setSnackbarOpen={setSnackbarOpen}
                                setIsLogin={setIsSignIn}
                                tokenValue={tokenValue}
                                label="Sign up with Google"
                                refreshCredits={refreshCredits}
                                humanCheckRef={humanCheckRef}
                            />

                            <Divider sx={{ my: { xs: 2, md: 4 }, color: 'var(--ink-3)' }} orientation="horizontal">
                                or
                            </Divider>

                            <Box
                                component="form"
                                noValidate
                                onSubmit={signUp}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    width: '100%',
                                    //maxWidth: 360,
                                    backgroundColor: 'var(--surface)',
                                }}
                            >
                                {/* autoComplete: "new-password" makes a phone's password
                                    manager offer a strong password here (not the saved
                                    login), and "username" ties what it saves to this email. */}
                                <TextField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setSignUpError({ email: '', password: '' })
                                    }}
                                    onFocus={() => setEmailFocused(true)}
                                    onBlur={() => setEmailFocused(false)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    error={!!signUpError.email}
                                    helperText={signUpError.email || ''}
                                />
                                <TextField
                                    label="Password"
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setSignUpError({ email: '', password: '' })
                                    }}
                                    onFocus={() => setPasswordFocused(true)}
                                    onBlur={() => setPasswordFocused(false)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    error={!!signUpError.password}
                                    helperText={signUpError.password || 'Password must be at least 6 characters, include letters and numbers'}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        borderRadius: 2,
                                        py: 1.35,
                                        mt: 0.75,
                                        boxShadow: '0 4px 14px rgba(25,118,210,0.22)',
                                        '&:hover': { boxShadow: '0 6px 20px rgba(25,118,210,0.32)' },
                                    }}
                                >
                                    {isEmailSignUp ? (
                                        <CircularProgress size={20} sx={{ color: 'white' }} />
                                    ) : (
                                        'Sign Up'
                                    )}
                                </Button>
                            </Box>
                            {/* Cloudflare Turnstile — INSIDE the card. The
                                widget itself shows Cloudflare branding when
                                visible, so no extra caption. */}
                            <Box ref={humanCheckRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <TurnStile
                                    ref={turnstileRef}
                                        setToken={onTurnstileToken}
                                    onError={onTurnstileError}
                                />
                            </Box>
                            <Box sx={{ textAlign: 'center', mt: 2.25 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'var(--ink-3)' }}>
                                    Already have an account?{' '}
                                    <Link component={RouterLink} to="/" underline="hover" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>
                                        Login
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </>)}
                {verificationPromptOpen && (
                    <>
                        <Box
                            sx={{
                                width: '100%',
                                backgroundColor: 'var(--surface)',
                                px: { xs: 3, sm: 4.5 },
                                py: { xs: 3, sm: 3.5 },
                                borderRadius: 3,
                                border: '1px solid var(--rule)',
                                boxShadow: '0 20px 60px rgba(15,31,61,0.10), 0 4px 16px rgba(25,118,210,0.06)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                                },
                            }}
                        >
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Verify Your Email
                                </Typography>
                            </Box>
                            <Box
                                component="form"
                                noValidate
                                onSubmit={signUp}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                    width: "100%",
                                }}
                            >
                                {/* Title */}
                                <Typography variant="body2" align="center" color="text.secondary">
                                    Please check the verification email in your inbox including spam folder.
                                </Typography>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ mb: 2 }}
                                >
                                    <CircularProgress size={24} />
                                    <Typography variant="subtitle1" sx={{ ml: 2, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                                        Detecting your verification...
                                    </Typography>
                                </Box>

                                <Divider />

                                {/* Resend Section */}
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }} >
                                        Didn’t get the email? Check spam folder
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="medium"
                                        fullWidth
                                        onClick={handleResend}
                                        disabled={countdown > 0}
                                        sx={{
                                            textTransform: "none",
                                            maxWidth: { xs: 125, sm: 140, lg: 180 }
                                        }}
                                    >
                                        {countdown > 0 ? `Resend (${countdown}s)` : "Resend Email"}
                                    </Button>
                                </Box>

                                {/* Login Section */}
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                        Already verified?
                                    </Typography>
                                    <Button
                                        component={RouterLink}
                                        to="/"
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        fullWidth
                                        sx={{
                                            textTransform: "none",
                                            maxWidth: { xs: 125, sm: 140, lg: 180 }
                                        }}
                                    >
                                        Login
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
            </Fade>
            <Snackbar
                open={snackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 5, mr: 1 }}
                onClose={() => setSnackbarOpen(false)}
            // message={snackbarMessage}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={notifySeverity(snackbarMessage)} sx={{ width: '100%', fontSize: '1.0rem' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default SignUp;